import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Footer } from "@/components/Footer";
import { PRIVACY_POLICY, TERMS } from "@/lib/legal";

const Section = ({ s }) => (
  <section className="mt-10">
    <h2 className="font-display font-bold text-base md:text-lg text-talanta-green">{s.title}</h2>
    {s.body?.map((p, i) => (
      <p key={i} className="mt-3 text-sm sm:text-base text-talanta-charcoal/80 leading-relaxed">{p}</p>
    ))}
    {s.bullets && (
      <ul className="mt-3 space-y-2">
        {s.bullets.map((b, i) => (
          <li key={i} className="flex gap-3 text-sm sm:text-base text-talanta-charcoal/80 leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-talanta-terracotta" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
    {s.after?.map((p, i) => (
      <p key={i} className="mt-3 text-sm sm:text-base text-talanta-charcoal/80 leading-relaxed">{p}</p>
    ))}
  </section>
);

export default function Legal({ doc }) {
  const data = doc === "terms" ? TERMS : PRIVACY_POLICY;
  const other = doc === "terms" ? { to: "/privacy", label: "Privacy Policy" } : { to: "/terms", label: "Terms of Use" };

  useEffect(() => {
    document.title = `${data.title} · Talanta Hela`;
    window.scrollTo(0, 0);
    return () => { document.title = "Talanta Hela — Monetizing Talent, Empowering Kenya"; };
  }, [data.title]);

  return (
    <div className="min-h-screen bg-talanta-cream" data-testid={`legal-page-${doc}`}>
      <header className="bg-talanta-green text-white">
        <div className="h-1 kenya-stripe" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-talanta-gold transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Talanta Hela
          </Link>
          <div className="mt-6 flex items-center gap-2 text-talanta-gold text-xs font-semibold uppercase tracking-[0.22em]">
            <ShieldCheck className="h-4 w-4" /> Legal
          </div>
          <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight">{data.title}</h1>
          <p className="mt-4 text-white/75 max-w-xl">{data.subtitle}</p>
          <p className="mt-3 text-xs text-white/50">{data.updated}</p>
        </div>
      </header>

      <main id="main" className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
        {data.sections.map((s) => <Section key={s.title} s={s} />)}
        <div className="mt-14 rounded-2xl border border-talanta-green/10 bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <span className="text-sm text-talanta-charcoal/70">Also read our {other.label}.</span>
          <Link to={other.to} data-testid="legal-switch-doc" className="text-sm font-semibold text-talanta-terracotta hover:underline">
            {other.label} →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
